import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import Combobox from "@/components/combobox";
import Select from "@/components/Select";
import Table from "@/components/Table";
import { MONTHS, REPORT_STATUS, YEARS } from "@/constants";
import api from "@/services/api";
import { captureError } from "@/services/error";

const TABLE_HEADER = [{ title: "Partenaire", key: "publisherName" }, { title: "Statut", key: "status" }, { title: "Stat" }];

const AdminReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    publisherId: searchParams.get("publisherId") || "",
    year: parseInt(searchParams.get("year")) || null,
    month: parseInt(searchParams.get("month")) || null,
    size: 25,
    page: parseInt(searchParams.get("page")) || 1,
    sortBy: searchParams.get("sortBy") || "createdAt",
  });
  const [options, setOptions] = useState({
    publishers: [],
    status: [],
  });
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const query = {};
        if (filters.status) query.status = filters.status;
        if (filters.publisherId) query.publisherId = filters.publisherId;
        if (filters.year) query.year = filters.year;
        if (filters.month) query.month = filters.month;
        query.size = filters.size;
        query.from = (filters.page - 1) * filters.size;
        query.sortBy = filters.sortBy;
        setSearchParams(new URLSearchParams(query));

        const res = await api.post("/admin-report/search", query);
        if (!res.ok) throw res;
        setData(res.data);
        setOptions(res.aggs);
        setTotal(res.total);
      } catch (error) {
        captureError(error, { extra: { filters } });
      }
      setLoading(false);
    };
    fetchData();
  }, [filters]);

  return (
    <div className="space-y-12 bg-white p-4 shadow-lg sm:p-12">
      <title>API Engagement - Rapports d'impacts - Administration</title>
      <div className="flex items-center justify-between">
        <h1 className="flex-1 text-2xl font-semibold">{total.toLocaleString("fr")} rapports d'impacts</h1>
      </div>

      <div className="border-grey-border space-y-6 overflow-x-auto border p-4 sm:p-6">
        <p className="font-bold">Filtrer les resultats</p>
        <div className="flex flex-col gap-4 border-b border-b-gray-900 pb-6 sm:flex-row sm:flex-wrap sm:items-center">
          <Combobox
            id="publisher-id"
            options={options.publishers.sort((a, b) => b.count - a.count).map((e) => ({ value: e._id, label: e.name, count: e.count }))}
            value={filters.publisherId}
            onSelect={(e) => (e ? setFilters({ ...filters, publisherId: e.value }) : null)}
            placeholder="Partenaire"
          />
          <Select
            options={MONTHS.map((e, i) => ({ value: i, label: e }))}
            value={filters.month}
            onChange={(e) => setFilters({ ...filters, month: e.value, year: filters.year || new Date().getFullYear() })}
            placeholder="Mois"
          />
          <Select options={YEARS.map((e) => ({ value: e, label: e }))} value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.value })} placeholder="Année" />
          <Combobox
            id="status"
            options={options.status.sort((a, b) => b.count - a.count).map((e) => ({ value: e._id, label: REPORT_STATUS[e._id] || e._id, count: e.count }))}
            value={filters.status}
            onSelect={(e) => (e ? setFilters({ ...filters, status: e.value }) : null)}
            placeholder="Statut"
          />
        </div>
        <Table
          caption="Liste des signalements"
          header={TABLE_HEADER}
          pagination
          page={filters.page}
          pageSize={filters.size}
          onPageChange={(page) => setFilters({ ...filters, page })}
          total={total}
          loading={loading}
          sortBy={filters.sortBy}
          onSort={(sortBy) => setFilters({ ...filters, sortBy })}
        >
          {data.map((item, i) => (
            <tr key={i} className={`${i % 2 === 0 ? "bg-table-even" : "bg-table-odd"} table-row`}>
              <td className="space-y-1 p-4">
                <Link className="link text-base font-bold" to={`/publisher/${item.publisherId}`}>
                  {item.publisherName}
                </Link>
                <div className="flex items-center gap-2">
                  <span className="bg-blue-cumulus-925 rounded-xl px-2 py-1 text-gray-700" style={{ fontSize: 12 }}>
                    {MONTHS[item.month]}
                  </span>
                  <span className="bg-blue-cumulus-925 rounded-xl px-2 py-1 text-gray-700" style={{ fontSize: 12 }}>
                    {item.year}
                  </span>
                </div>
              </td>
              <td className="px-4">
                {item.status === "SENT" ? (
                  <>
                    <div className="flex items-center gap-2">
                      <div className="bg-success h-2 w-2 rounded-full" />
                      <p className="flex-1 text-sm font-bold">Envoyé le {new Date(item.sentAt).toLocaleDateString("fr")}</p>
                    </div>
                    <p className="text-xs">{item.sentTo.length ? item.sentTo.join(", ") : "Aucun destinataire"}</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <div className="bg-error h-2 w-2 rounded-full" />
                      <p className="flex-1 text-sm font-bold">Non envoyé</p>
                    </div>
                    <p className="flex-1 text-xs">{REPORT_STATUS[item.status] || item.status}</p>
                  </>
                )}
              </td>

              <td className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex min-w-[200px] flex-1 flex-col gap-1">
                    {item.data?.receive !== undefined && item.data?.receive.general !== undefined && <p className="text-xs">Pas de statistiques disponibles</p>}
                    {item.data?.receive !== undefined && (
                      <>
                        <p className="text-xs">
                          Redirections reçues : <span className="font-bold">{(item.data.receive.general?.traffic || item.data.receive.click || 0).toLocaleString("fr")}</span>
                        </p>
                        <p className="text-xs">
                          Candidatures reçues : <span className="font-bold">{(item.data.receive.general?.apply || item.data.receive.apply || 0).toLocaleString("fr")}</span>
                        </p>
                      </>
                    )}
                    {item.data?.send !== undefined && (
                      <>
                        <p className="text-xs">
                          Redirections envoyées : <span className="font-bold">{(item.data.send.general?.traffic || item.data.send.click || 0).toLocaleString("fr")}</span>
                        </p>
                        <p className="text-xs">
                          Candidatures envoyées : <span className="font-bold">{(item.data.send.general?.apply || item.data.send.apply || 0).toLocaleString("fr")}</span>
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </div>
  );
};

export default AdminReport;
